import React from "react";

/** Question sources whose answers may not be submitted without a structured evidence record. */
export const EVIDENCE_QUESTION_SOURCES = ["official-frame-v2", "boundary-gate-v1"];

export const SOURCE_TYPE_LABELS = {
  manufacturer_document: "제조사 제품 사양서",
  product_label: "제품 라벨",
  composition_sheet: "성분·재질표",
  drawing: "도면",
  photo: "제품 사진",
  test_report: "시험성적서",
  user_statement: "사용자 확인",
  other: "기타",
};

const ALL_SOURCE_TYPES = Object.keys(SOURCE_TYPE_LABELS);

export const questionRequiresEvidence = (question) =>
  EVIDENCE_QUESTION_SOURCES.includes(question?.source);

/**
 * Boundary questions carry two answers that are statements about the evidence itself — "cannot tell"
 * and "not applicable". Demanding a document for those is what turns an evidence gate into a
 * guess-and-attach-a-label gate, so the server marks them `evidence_required: false`.
 */
export const optionRequiresEvidence = (question, option) =>
  questionRequiresEvidence(question) && option?.evidence_required !== false;

/** Source classes this option accepts. Empty means the server did not restrict them. */
export const admissibleSourcesOf = (option) => {
  const list = Array.isArray(option?.admissible_sources) ? option.admissible_sources : [];
  return list.filter((value) => ALL_SOURCE_TYPES.includes(value));
};

export const emptyOfficialEvidence = () => ({
  source_type: "",
  fact_summary: "",
  document_reference: "",
  confirmed_facts: [],
});

export const isOfficialEvidenceReady = (value, option) => {
  if (option && option.evidence_required === false) return true;
  const requiredFacts = Array.isArray(option?.required_facts) ? option.required_facts : [];
  const confirmedFacts = Array.isArray(value?.confirmed_facts) ? value.confirmed_facts : [];
  const admissible = admissibleSourcesOf(option);
  const sourceType = value?.source_type;
  if (!sourceType) return false;
  // Mirrors the server rule. Client-side it is only a courtesy — the server refuses the same
  // combination — but without it the user fills the whole form before being told the source class
  // cannot establish this fact.
  if (admissible.length > 0 && !admissible.includes(sourceType)) return false;
  return String(value?.fact_summary || "").trim().length >= 3
    && requiredFacts.every((fact) => confirmedFacts.includes(fact));
};

export const buildOfficialEvidence = (value, option) => {
  if (option && option.evidence_required === false) return null;
  const requiredFacts = Array.isArray(option?.required_facts) ? option.required_facts : [];
  const confirmedFacts = Array.isArray(value?.confirmed_facts) ? value.confirmed_facts : [];
  return {
    source_type: value.source_type,
    fact_summary: String(value.fact_summary || "").trim(),
    document_reference: String(value.document_reference || "").trim() || null,
    confirmed_facts: confirmedFacts.filter((fact) => requiredFacts.includes(fact)),
  };
};

export default function OfficialFrameEvidenceForm({
  value,
  onChange,
  disabled = false,
  compact = false,
  requiredFacts = [],
  admissibleSources = [],
  observableKo = "",
}) {
  if (!value) return null;
  const sourceTypes = admissibleSources.length > 0
    ? admissibleSources.filter((item) => ALL_SOURCE_TYPES.includes(item))
    : ALL_SOURCE_TYPES;
  const update = (key, next) => onChange && onChange({ ...value, [key]: next });
  const toggleFact = (fact, checked) => {
    const current = Array.isArray(value.confirmed_facts) ? value.confirmed_facts : [];
    update(
      "confirmed_facts",
      checked ? [...new Set([...current, fact])] : current.filter((item) => item !== fact),
    );
  };
  const controlStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: compact ? "6px 7px" : "7px 8px",
    border: "1px solid #d0d5dd",
    borderRadius: 6,
    background: "#fff",
    color: "#344054",
    fontSize: compact ? 11.5 : 12,
    fontFamily: "inherit",
  };
  return (
    <div style={{ display: "grid", gap: 6, padding: compact ? 7 : 9, marginTop: 9, border: "1px solid #d0d5dd", borderRadius: 7, background: "#f8fafc" }}>
      <div style={{ fontSize: compact ? 11.5 : 12, fontWeight: 700, color: "#344054" }}>
        선택 근거 기록 (필수)
      </div>
      {observableKo && (
        <div style={{ fontSize: 10.5, lineHeight: 1.45, color: "#667085" }}>
          확인 위치: {observableKo}
        </div>
      )}
      <select value={value.source_type} onChange={(event) => update("source_type", event.target.value)} disabled={disabled} style={controlStyle}>
        <option value="">근거 출처 선택</option>
        {sourceTypes.map((item) => (
          <option key={item} value={item}>{SOURCE_TYPE_LABELS[item]}</option>
        ))}
      </select>
      {admissibleSources.length > 0 && (
        <div style={{ fontSize: 10.5, lineHeight: 1.45, color: "#8a4b08" }}>
          이 사실은 위 자료로만 확인할 수 있습니다. 사용자 진술이나 사진은 근거가 되지 않습니다.
        </div>
      )}
      <textarea value={value.fact_summary} onChange={(event) => update("fact_summary", event.target.value)} disabled={disabled} rows={compact ? 2 : 3} maxLength={1000} placeholder="분기를 뒷받침하는 제품 사실을 3자 이상 입력" style={{ ...controlStyle, resize: "vertical" }} />
      <input value={value.document_reference} onChange={(event) => update("document_reference", event.target.value)} disabled={disabled} maxLength={300} placeholder="문서명·페이지·파일 식별자 (선택)" style={controlStyle} />
      {requiredFacts.length > 0 && (
        <fieldset style={{ margin: 0, padding: 7, border: "1px solid #d0d5dd", borderRadius: 6 }}>
          <legend style={{ padding: "0 4px", fontSize: compact ? 11 : 11.5, fontWeight: 700, color: "#344054" }}>
            자료에서 직접 확인한 필수 사실
          </legend>
          <div style={{ display: "grid", gap: 6 }}>
            {requiredFacts.map((fact) => (
              <label key={fact} style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: compact ? 11 : 11.5, lineHeight: 1.45, color: "#475467" }}>
                <input
                  type="checkbox"
                  checked={Array.isArray(value.confirmed_facts) && value.confirmed_facts.includes(fact)}
                  onChange={(event) => toggleFact(fact, event.target.checked)}
                  disabled={disabled}
                  style={{ marginTop: 2 }}
                />
                <span>{fact}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
      <div style={{ fontSize: 10.5, lineHeight: 1.45, color: "#667085" }}>
        실제 자료로 확인한 사실만 체크하세요. 모든 필수 사실이 확인되어야 분기를 제출할 수 있습니다.
      </div>
    </div>
  );
}
